//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import cloneDeep from 'lodash/fp/cloneDeep';
import _ from 'underscore';

import { BULK_ACTIONS_ACTIONS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';
import { BulkActionsAction } from '../bulk-actions-actions/bulk-actions-actions';
import { IBulkActionsState } from '../model/AppState';
import { BulkActionNames } from '../model/BulkActionNames';

const initialState: IBulkActionsState = {
  actionLabelsModal: {
    isBulkAddLabelsModalOpen: false,
    isRemoveLabelsModal: false,
    selectedItems: [],
  },
  allItemDetails: {},
  confirmationModal: '',
  currentCollection: null,
  // These actions are context dependent, so we hide them by default and introduce
  // them only when we find ourselves in an appropriate conteext
  disabledActions: [
    BulkActionNames.REMOVE_FROM_COLLECTION,
    BulkActionNames.REMOVE_FROM_GALLERY,
    BulkActionNames.REMOVE_FROM_GOOGLE_ASSET_FEED,
    BulkActionNames.PUBLISH_TO_GOOGLE_ASSET_FEED,
    BulkActionNames.REMOVE_FROM_LIBRARY,
    BulkActionNames.DELETE_CREATOR_CAMPAIGN_CONTENT,
    BulkActionNames.REPORT_AI_CONTENT,
  ],
  isCollectionsEnabled: null,
  isLabelsEnabled: null,
  isLibraryEnabled: null,
  selectedItems: [],
  selectedItemsForCaptions: {},
  captionTurnOnOffState: '',
  isVideoActionInProgress: false,
  rightsRequestStatuses: {},
  selectedItemsForContentSource: [],
};

export default function BulkActionsReducer(
  state: IBulkActionsState = initialState,
  action: BulkActionsAction,
): IBulkActionsState {
  switch (action.type) {
    case BULK_ACTIONS_ACTIONS.UNSELECT_ITEM:
      const removedCuid = action.payload;
      const itemsBeforeUnselect = state.selectedItems;
      const itemsAfterUnselect = _(itemsBeforeUnselect).filter((item) => {
        return item.cuid !== removedCuid;
      });

      return Object.assign({}, state, {
        selectedItems: itemsAfterUnselect,
      });
    case BULK_ACTIONS_ACTIONS.UNSELECT_ITEMS:
      const itemsAfterRangeUnselect = state.selectedItems.filter((item) => {
        return !action.removedCuids.includes(item.cuid);
      });

      return {
        ...state,
        selectedItems: itemsAfterRangeUnselect,
      };
    case BULK_ACTIONS_ACTIONS.SELECT_ITEM:
      const newItem = action.payload;
      const itemsBeforeSelect = state.selectedItems;
      const itemsAfterSelect = [...itemsBeforeSelect, newItem];

      return Object.assign({}, state, {
        selectedItems: itemsAfterSelect,
      });
    case BULK_ACTIONS_ACTIONS.SELECT_ITEMS:
      const allSelectedItemsAfterRangeSelect = [
        ...state.selectedItems,
        ...action.newItems,
      ];

      // Ensures we don't include duplicates for items that were already selected
      const uniqueItemsAfterRangeSelect = _(
        allSelectedItemsAfterRangeSelect,
      ).uniq((item) => item.cuid);

      return {
        ...state,
        selectedItems: uniqueItemsAfterRangeSelect,
      };
    case BULK_ACTIONS_ACTIONS.CLEAR_SELECTED_ITEMS:
      return Object.assign({}, state, {
        selectedItems: [],
        rightsRequestStatuses: {},
      });
    case BULK_ACTIONS_ACTIONS.OPEN_BULK_ADD_LABELS_MODAL:
      return Object.assign({}, state, {
        actionLabelsModal: {
          ...state.actionLabelsModal,
          isBulkAddLabelsModalOpen: true,
          isRemoveLabelsModal: false,
        },
      });
    case BULK_ACTIONS_ACTIONS.CLOSE_BULK_ADD_LABELS_MODAL:
      return Object.assign({}, state, {
        actionLabelsModal: {
          ...state.actionLabelsModal,
          isBulkAddLabelsModalOpen: false,
          isRemoveLabelsModal: false,
        },
      });
    case BULK_ACTIONS_ACTIONS.OPEN_BULK_REMOVE_LABELS_MODAL:
      return Object.assign({}, state, {
        actionLabelsModal: {
          ...state.actionLabelsModal,
          isBulkAddLabelsModalOpen: true,
          isRemoveLabelsModal: true,
        },
      });
    case BULK_ACTIONS_ACTIONS.SET_ITEMS_BULK_ADD_LABELS_MODAL:
      return Object.assign({}, state, {
        actionLabelsModal: {
          ...state.actionLabelsModal,
          selectedItems: action.mediaItems,
        },
      });
    case BULK_ACTIONS_ACTIONS.OPEN_ACTION_CONFIRMATION_MODAL:
      return Object.assign({}, state, {
        confirmationModal: action.payload,
      });
    case BULK_ACTIONS_ACTIONS.CLOSE_ACTION_CONFIRMATION_MODAL:
      return Object.assign({}, state, {
        confirmationModal: '',
      });
    case BULK_ACTIONS_ACTIONS.SET_ITEM_DETAILS:
      return {
        ...state,
        allItemDetails: {
          ...state.allItemDetails,
          [action.payload.cuid]: action.payload.itemDetails,
        },
      };
    case BULK_ACTIONS_ACTIONS.SET_FEATURES:
      return Object.assign({}, state, {
        isCollectionsEnabled: action.payload.isCollectionsEnabled,
        isLabelsEnabled: action.payload.isLabelsEnabled,
        isLibraryEnabled: action.payload.isLibraryEnabled,
      });
    case BULK_ACTIONS_ACTIONS.ENABLE_BULK_ACTION:
      const actionsDisabledAfterEnable = _(state.disabledActions).without(
        action.payload,
      );

      return Object.assign({}, state, {
        disabledActions: actionsDisabledAfterEnable,
      });
    case BULK_ACTIONS_ACTIONS.DISABLE_BULK_ACTION:
      const actionsDisabledBeforeDisable = cloneDeep(state.disabledActions);
      const actionsDisabledAfterDisable = [
        ...actionsDisabledBeforeDisable,
        action.payload,
      ];

      return Object.assign({}, state, {
        disabledActions: actionsDisabledAfterDisable,
      });
    case BULK_ACTIONS_ACTIONS.SET_CURRENT_COLLECTION:
      return Object.assign({}, state, {
        currentCollection: action.payload,
      });
    case BULK_ACTIONS_ACTIONS.CLEAR_CURRENT_COLLECTION:
      return Object.assign({}, state, {
        currentCollection: null,
      });
    case BULK_ACTIONS_ACTIONS.RECEIVE_RIGHTS_STATUSES:
      return {
        ...state,
        rightsRequestStatuses: {
          ...state.rightsRequestStatuses,
          ...action.rightsMap,
        },
      };

    case BULK_ACTIONS_ACTIONS.SET_SELECTED_ITEMS_FOR_CAPTIONS:
      return Object.assign({}, state, {
        selectedItemsForCaptions: action.mediaItems,
      });

    case BULK_ACTIONS_ACTIONS.CLEAR_SELECTED_ITEMS_FOR_CAPTIONS:
      return Object.assign({}, state, {
        selectedItemsForCaptions: {},
      });

    case BULK_ACTIONS_ACTIONS.SET_CAPTION_TURN_ON_OFF_STATE:
      return Object.assign({}, state, {
        captionTurnOnOffState: action.captionTurnOnOffState,
      });

    case BULK_ACTIONS_ACTIONS.SET_IS_VIDEO_ACTION_IN_PROGRESS:
      return {
        ...state,
        isVideoActionInProgress: action.isVideoActionInProgress,
      };

    case BULK_ACTIONS_ACTIONS.SET_SELECTED_ITEMS_FOR_CONTENT_SOURCE:
      return {
        ...state,
        selectedItemsForContentSource: action.items,
      };

    default:
      return state;
  }
}
